import Divider from '@mui/material/Divider'
import Grid from '@mui/material/Grid2'
import { personAPI } from 'api/PersonAPI'
import ErrorOverlay from 'Components/General/ErrorOverlay'
import { getCvCopyInitialData } from 'Components/reusable/DataContext/InitialData'
import { copyProfileSchema } from 'Components/reusable/DataContext/ValidationSchema'
import { useCompanyPermissions, useUser } from 'hooks'
import { useAccountNetworks, useBrokerNetworks } from 'hooks/networks'
import { orderBy } from 'lodash'
import { useMemo, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { CVOption, ICv } from 'types/cvsInterfaces'
import { chooseDBTranslation } from 'utils/translations'
import ContactSalesCard from './ContactSalesCard/ContactSalesCard'
import CopyProfileModal from './CopyProfileModal/CopyProfileModal'
import ProfilePageCards from './ProfilePageCards/ProfilePageCards'
import ProfileProgressModal from './ProfileProgressModal/ProfileProgressModal'
import ProfileToolbarItems from './ProfileToolbarItems/ProfileToolbarItems'
import CvUpdateHistoryModal from './CvUpdateHistoryModal/CvUpdateHistoryModal'
import CvParseModal from './CvParseModal'
import { useLocation, useParams, useNavigate } from 'react-router'
import LoadingIndicator from 'Components/reusable/LoadingIndicator'
import PersonAllocationView from 'pages/Allocation/PersonAllocationView/PersonAllocationView'
import ProfileNotesSection from './ProfileNotesSection/'
import { useProfileState } from './useProfileState'
import { useProfileData } from './useProfileData'
import { removeCvAndChooseFallback } from './profileCvLogic'
import WizardResumePrompt from './CvWizard/WizardResumePrompt'
import WizardStartCard from './CvWizard/WizardStartCard'

const Profile = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const { user, ready, groups, features } = useUser()
  const { t, i18n } = useTranslation()
  const {
    state,
    setField,
    patch,
    setPersonData,
    setSelectedCV,
    setBackendError,
    setCvUpdatedAt,
    setCvLoading,
    setCopy,
    setEdit,
    setProgressModal,
    setProfileProgress,
    setOpenHistory,
    setUpdateCvProgress,
    setProgressCV,
    setCvParse,
  } = useProfileState()
  const {
    personData,
    selectedCV,
    backendError,
    cvUpdatedAt,
    isEmpty,
    personDataLoading,
    emptyLoading,
    cvLoading,
    copy,
    edit,
    original,
    progressModal,
    profileProgress,
    fullCV,
    openHistory,
    progressCV,
    personTeamAccess,
    cvParse,
    parseCredits,
    sectionLoadingState,
  } = state
  const { salesAccess, companyAdminAccess } = useCompanyPermissions(personData?.organizationId ?? undefined)
  const { networks: viewerNetworks } = useBrokerNetworks()
  const { networks: accountNetworks } = useAccountNetworks(personData?.accountId)
  const { profileId: id } = useParams()

  // Calculate effective personId. Use `profileId` from the URL and
  // fall back to the user's personId. If the computed id is null, we
  // can throw a 404
  const personId = id ? parseInt(id, 10) : user?.Person?.id

  useProfileData({
    personId,
    ready,
    navigate,
    locationState: location.state,
    user,
    groups,
    i18n,
    t,
    state,
    setField,
    patch,
    setBackendError,
    setProgressCV,
    setProfileProgress,
  })

  const profileNotesEnabled = features.includes('profileNotes')
  const isOwnerProfile = !!(user?.Person?.id && personId && user.Person.id === personId)
  const canManageProfileNotes = !!(groups.includes('admin') || salesAccess || companyAdminAccess)
  const canAccessProfileNotes = isOwnerProfile || canManageProfileNotes
  const canViewArchivedProfileNotes = !!(groups.includes('admin') || companyAdminAccess)

  const allSectionsLoaded = useMemo(() => {
    return Object.values(sectionLoadingState).every(loading => !loading)
  }, [sectionLoadingState])

  const chooseName = useCallback(
    (cv: ICv, index: number) => {
      const translation = chooseDBTranslation(i18n, cv)
      const translationName = translation.name
      const cvName = cv.translations.find(tr => tr.name !== undefined && tr.name !== null && tr.name.length > 0)?.name

      return translationName ? translationName : (cvName ?? 'CV_' + index)
    },
    [i18n]
  )

  const CVOptions: CVOption[] = useMemo(() => {
    let options: CVOption[] = []

    if (personData && personData.CVs) {
      for (let i = 0; i < personData.CVs.length; i++) {
        const cv = personData.CVs[i]
        options.push({
          key: cv.id,
          label: chooseName(cv, i),
          data: cv,
        })
      }
    }

    options = orderBy(options, ['data.id'], ['asc'])

    return options
  }, [personData, chooseName])

  const updateCvList = useCallback(
    (type: string, data: ICv) => {
      setPersonData(previous => {
        if (!previous || !previous.CVs || !data) return previous

        if (type === 'edit') {
          return {
            ...previous,
            CVs: previous.CVs.map(item => (item.id === data.id ? data : item)),
          }
        }
        if (type === 'create') {
          return {
            ...previous,
            CVs: [...previous.CVs, data],
          }
        }
        if (type === 'delete') {
          const { cvs, selectedCv } = removeCvAndChooseFallback({
            cvs: previous.CVs,
            deletedCvId: data.id,
            selectedCvId: selectedCV?.id,
          })

          if (selectedCv) {
            setSelectedCV(selectedCv)
          }
          return {
            ...previous,
            CVs: cvs,
          }
        }
        return previous
      })
    },
    [selectedCV, setPersonData, setSelectedCV]
  )

  const closeCopyModal = useCallback(
    (type: 'edit' | 'create' | 'delete', data: unknown) => {
      setCvLoading(true)
      setEdit(undefined)
      setCopy(false)
      if (data && typeof data === 'object' && 'id' in data) {
        const cv = data as ICv

        if (type === 'edit' || type === 'create') {
          updateCvList(type, cv)
          setSelectedCV(cv)
        } else if (type === 'delete' && personData && personData.CVs) {
          updateCvList(type, cv)
        }
      }
    },
    [personData, updateCvList, setCvLoading, setEdit, setCopy, setSelectedCV]
  )

  const getProfilePageContent = useCallback(
    (selectedCv: ICv) => {
      if (personData && selectedCV) {
        const showWizardStart = allSectionsLoaded && isEmpty && (personData.id === user?.Person?.id || salesAccess)

        return (
          <>
            {showWizardStart && <WizardStartCard personId={personData.id} cvId={selectedCV.id} />}
            <ProfilePageCards
              selectedCv={selectedCv}
              personData={personData}
              cvLoading={cvLoading}
              sectionLoadingState={sectionLoadingState}
              setCvLoading={setCvLoading}
              setCvUpdatedAt={setCvUpdatedAt}
              salesAccess={salesAccess || personTeamAccess}
              learningGoalsAccess={salesAccess || companyAdminAccess}
              accountNetworks={accountNetworks}
              setUpdateCvProgress={setUpdateCvProgress}
            />
          </>
        )
      }
    },
    [
      personData,
      selectedCV,
      allSectionsLoaded,
      isEmpty,
      user?.Person?.id,
      salesAccess,
      cvLoading,
      sectionLoadingState,
      personTeamAccess,
      companyAdminAccess,
      accountNetworks,
      setCvLoading,
      setCvUpdatedAt,
      setUpdateCvProgress,
    ]
  )

  const isBroker = useCallback((): boolean => {
    const commonNetworks = viewerNetworks?.filter(
      network => network.id === accountNetworks?.find(n => n.id === network.id)?.id
    )
    if (commonNetworks) {
      for (const commonNetwork of commonNetworks) {
        if (
          commonNetwork?.NetworkBrokers?.find(broker => broker.accountId === user?.id) &&
          commonNetwork.salesContactsVisible
        ) {
          return true
        }
      }
    }
    return false
  }, [viewerNetworks, accountNetworks, user?.id])

  const additionalItems = useMemo(() => {
    const isOtherOrg = personData?.organizationId && user?.organizationId !== personData?.organizationId
    const isBrokerUser = isOtherOrg && isBroker()
    const isNotOwnerProfile = personId != user?.Person?.id

    return (
      <Grid>
        {isBrokerUser && <ContactSalesCard personData={personData} />}
        {isNotOwnerProfile && <PersonAllocationView collapsible={true} />}
        {(isNotOwnerProfile || isBrokerUser) && <Divider sx={{ mb: 10, mt: 2 }} />}
      </Grid>
    )
  }, [personData, user?.organizationId, user?.Person?.id, personId, isBroker])

  if (backendError && backendError.name !== 'CanceledError' && backendError.name !== 'AbortError') {
    return <ErrorOverlay error={backendError} setOpen={setBackendError} />
  }

  // Only block on person data loading, allow sections to load progressively
  if (personDataLoading || emptyLoading) {
    return <LoadingIndicator />
  }

  return (
    <>
      {!cvLoading && additionalItems}
      {selectedCV && (
        <>
          {fullCV && getProfilePageContent(fullCV)}
          {personData && <WizardResumePrompt personId={personData.id} cvId={selectedCV.id} />}
          {personData && personId && profileNotesEnabled && canAccessProfileNotes && (
            <ProfileNotesSection
              personId={personId}
              enabled={profileNotesEnabled && canAccessProfileNotes}
              canManageNotes={canManageProfileNotes}
              canViewArchived={canViewArchivedProfileNotes}
              isOwnerProfile={isOwnerProfile}
            />
          )}
          <ProfileToolbarItems
            cvLoading={cvLoading}
            cvUpdatedAt={cvUpdatedAt}
            profileProgress={profileProgress}
            setCopy={setCopy}
            setEdit={setEdit}
            setProgressModal={setProgressModal}
            selectedCV={selectedCV}
            setSelectedCV={setSelectedCV}
            personData={personData}
            setPersonData={setPersonData}
            salesAccess={salesAccess || personTeamAccess}
            setOpenHistory={setOpenHistory}
            setCvParse={setCvParse}
            CVOptions={CVOptions}
            parseCredits={parseCredits}
            updateCvList={updateCvList}
          />
          {copy && personData && personData.CVs && (
            <CopyProfileModal
              item={edit}
              setOriginal={() => setSelectedCV(original)}
              onClose={() => {
                setCopy(false)
                setEdit(undefined)
              }}
              onAdvancedClose={closeCopyModal}
              schema={copyProfileSchema()}
              initialData={getCvCopyInitialData(selectedCV)}
              maxWidth="xs"
              fullWidth={true}
              localeBase="profile"
              api={personAPI}
              cvId={selectedCV.id}
              disableDelete={personData.CVs.length <= 1 || personData.defaultCvId === selectedCV.id}
              noEscClose
            />
          )}
          {progressModal && progressCV && (
            <ProfileProgressModal
              onClose={() => setProgressModal(false)}
              cv={progressCV}
              header={t('profile.progressTitle')}
              maxWidth="xs"
              fullWidth={true}
              noEscClose
            />
          )}
          {openHistory && (
            <CvUpdateHistoryModal
              cvId={openHistory}
              onClose={() => setOpenHistory(undefined)}
              header={t('cvHistory.header')}
              noEscClose
            />
          )}
          {cvParse && (
            <CvParseModal
              personId={personId}
              onClose={(cv?: ICv) => {
                if (cv && cv.id) {
                  updateCvList('create', cv)
                  setSelectedCV(cv)
                }
                setCvParse(false)
              }}
              header={t('profile.parseModal')}
              maxWidth="lg"
              fullWidth={true}
              noEscClose
            />
          )}
        </>
      )}
    </>
  )
}

export default Profile
